/**
 * LevelToggle — vertical tier/level picker for multi-level products.
 *
 * Renders a vertical sliding ButtonGroup with one button per available tier.
 * Highest tier is at the top (reversed), mirroring a building's floors.
 *
 * Hidden when `capabilities.supportsLevelSelector` is false (single-level
 * products or products with only one distinct ref_tier).
 *
 * Tier label resolution (in priority order):
 *   1. `name_original` from TiersData, scoped to the product's building
 *      - length ≤ 2  → used directly as button label
 *      - length > 2  → shown in Tooltip; button shows short fallback
 *   2. Short fallback: ref_tier === 0 → t('common.floors_ground_floor_short')
 *                      ref_tier > 0  → String(ref_tier)
 */
import { useCatalog } from '../../../../catalog/index.js'
import { useProduct } from '../../../index.js'

import { useI18n }    from '../../../../../shared/i18n/useI18n.js'
import {
  getBuildingIdCadFromIdCad,
  findTierByRefTier,
} from '../../../../../shared/utils/idCadUtils.js'

import { Button, ButtonGroup } from '../../../../../shared/ui/Button/index.js'
import { Tooltip }             from '../../../../../shared/ui/Tooltip/index.js'

import styles from './LevelToggle.module.scss'
import { getTierName } from '../../../../../shared/utils/productFormatters.js'

export function LevelToggle({ isMobile }) {
  const { t } = useI18n()
  const { capabilities, viewerState, viewerActions, selection } = useProduct()
  const { tiers: tiersData } = useCatalog()

  if (!capabilities.supportsLevelSelector) return null

  const { availableLevels } = capabilities
  const { selectedLevelId }    = viewerState
  const { setSelectedLevelId } = viewerActions

  const buildingId = getBuildingIdCadFromIdCad(selection.product?.id_cad)

  // Fall back to the first available level when selectedLevelId is not yet set.
  const effectiveLevel = selectedLevelId ?? availableLevels[0] ?? null

  // Display highest floor at the top — reverse the sorted-ascending array. On mobile - when horizontal toggle, show the buttons in ascending order (left-to-right)
  const orderedLevels = isMobile ? [...availableLevels] : [...availableLevels].reverse()

  // Buttons are wrapped in Tooltip, so ButtonGroup can't auto-detect selected.
  const selectedIndex = orderedLevels.indexOf(effectiveLevel)

  function tierEntry(refTier) {
    const tierData = findTierByRefTier(refTier, tiersData, buildingId) ?? {}
    const nameLong = getTierName(tierData, true)
    const nameShort = getTierName(tierData)
    return { buttonLabel: nameShort, tooltipTitle: nameLong ?? nameShort}
  }

  const isVertical = !isMobile

  return (
    <div className={styles.root}>
      <ButtonGroup
        vertical={isVertical}
        sliding
        size={isMobile ? 'medium' : 'large'}
        selectedIndex={selectedIndex}
        aria-label={t('product.viewer_level_select')}
        style={
          isVertical
          ? { height: `calc(${orderedLevels.length} * var(--_button-group-track-size) + 2px)` }
          : { width: `calc(${orderedLevels.length} * var(--_button-group-track-size) + 2px)` }
        }
      >
        {orderedLevels.map((level) => {
          const { buttonLabel, tooltipTitle } = tierEntry(level)
          const active = level === effectiveLevel
          return (
            <Tooltip key={level} title={t('product.show_level', { level: tooltipTitle })} placement="left" noWrap>
              <Button
                type="button"
                kind="icon"
                size={isMobile ? 'medium' : 'large'}
                selected={active}
                aria-pressed={active}
                aria-label={tooltipTitle}
                onClick={() => setSelectedLevelId(level)}
              >
                <span className={styles.levelLabel}>{buttonLabel}</span>
              </Button>
            </Tooltip>
          )
        })}
      </ButtonGroup>
    </div>
  )
}
